import { useEffect, useMemo, useState } from "react";

// ─── DESIGN TOKENS — match Sirat Log Ledger / Protein Focus ─────────────
const C = {
  ink:   '#f4eee0',
  dim:   'rgba(244,238,224,0.62)',
  faint: 'rgba(244,238,224,0.35)',
  coffee:'#c9a07a',
  cream: '#e9dfc7',
  amber: '#f5b342',
  cyan:  '#22d3ee',
  green: '#00e676',
  coral: '#f08a6b',
};
const FS = "'Geist', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
const FM = "'Geist Mono', ui-monospace, 'SF Mono', Menlo, monospace";

// Per-shot espresso macros — ground beans, no sugar. ~30ml, trace calories.
const ESPRESSO = {
  id: "ground_espresso",
  name: "Espresso shot",
  calories: 3,
  protein: 0.1,
  carbs: 0,
  fat: 0,
  fiber: 0,
  sugar: 0,
  sodium: 5,
  servingG: 30,
  servingLabel: "1 shot (30ml)",
  halal: true,
};

// Canderel with Sucralose stick (1g). Per-stick: ~4 kcal, ~1g carb (bulking
// agent maltodextrin) — sucralose itself is calorie-free but the carrier
// adds a trace. Sugar = 0 (not sucrose), so it stays clean for the junk %.
const SWEETENER = {
  id: "canderel_sucralose",
  name: "Canderel sucralose",
  calories: 4,
  protein: 0,
  carbs: 1,
  fat: 0,
  fiber: 0,
  sugar: 0,
  sodium: 0,
  servingG: 1,
  servingLabel: "1 stick (1g)",
  halal: true,
};

const VOLUMES = [30, 50, 80, 100];
const SHOTS = [1, 2];
const SWEETENERS = [0, 1, 2];

function genId() {
  return 'cf_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

// ─── Quick-log helper ────────────────────────────────────────────────────
// Reads the last-saved combination from localStorage (same keys the overlay
// writes) and logs a single composed cappuccino entry. Used by the `+` chip
// on the Quick Add tile so a single tap re-logs the previous combo without
// opening the modal. Falls back to sensible defaults on first use.
function shortenMilkName(m) {
  if (!m) return "milk";
  return (m.name || "")
    .replace(/^Clover\s+/i, "Clover ")
    .replace(/^Woolworths\s+/i, "Woolies ")
    .replace(/^Almond Breeze\s+/i, "Almond ")
    .replace(/Milk\s*$/i, "milk")
    .trim();
}

function buildCappuccinoSynth({ milk, milkMl, shots, sweeteners }) {
  const baseG = Math.max(1, milk?.servingG || 250);
  const scale = milkMl / baseG;
  const pour = {
    protein:  (milk?.protein  || 0) * scale,
    calories: (milk?.calories || 0) * scale,
    carbs:    (milk?.carbs    || 0) * scale,
    fat:      (milk?.fat      || 0) * scale,
    fiber:    (milk?.fiber    || 0) * scale,
    sugar:    (milk?.sugar    || 0) * scale,
    sodium:   (milk?.sodium   || 0) * scale,
  };
  const totalKcal = Math.round((shots * ESPRESSO.calories) + pour.calories + (sweeteners * SWEETENER.calories));
  const totalP    = Math.round(((shots * ESPRESSO.protein) + pour.protein + (sweeteners * SWEETENER.protein)) * 10) / 10;
  const totalC    = Math.round(((shots * ESPRESSO.carbs)   + pour.carbs   + (sweeteners * SWEETENER.carbs))   * 10) / 10;
  const totalF    = Math.round(((shots * ESPRESSO.fat)     + pour.fat     + (sweeteners * SWEETENER.fat))     * 10) / 10;
  const totalFi   = Math.round(((shots * ESPRESSO.fiber)   + pour.fiber   + (sweeteners * SWEETENER.fiber))   * 10) / 10;
  const totalSu   = Math.round(((shots * ESPRESSO.sugar)   + pour.sugar   + (sweeteners * SWEETENER.sugar))   * 10) / 10;
  const totalSo   = Math.round((shots * ESPRESSO.sodium)   + pour.sodium  + (sweeteners * SWEETENER.sodium));
  const totalG    = (shots * 30) + milkMl + (sweeteners * 1);
  const sweetenerSuffix = sweeteners > 0 ? ` · ${sweeteners}× Canderel` : '';
  const food = {
    id: genId(),
    name: `Cappuccino · ${shortenMilkName(milk)} ${milkMl}ml${shots > 1 ? ` · ${shots} shots` : ''}${sweetenerSuffix}`,
    category: 'Beverages',
    calories: totalKcal,
    protein:  totalP,
    carbs:    totalC,
    fat:      totalF,
    fiber:    totalFi,
    sugar:    totalSu,
    sodium:   totalSo,
    servingG: totalG,
    servingLabel: `${shots > 1 ? `${shots} shots` : '1 shot'} + ${milkMl}ml milk${sweetenerSuffix}`,
    halal: true,
  };
  return { food, totalG, totalKcal };
}

export function quickLogLastCappuccino({ milkProducts, addFoodEntry, today }) {
  if (!milkProducts?.length || !addFoodEntry) return null;
  let selectedMilkId = milkProducts[0].id;
  let milkMl = 50;
  let shots = 1;
  let sweeteners = 0;
  try {
    selectedMilkId = localStorage.getItem('cappuccinoFocusMilk') || selectedMilkId;
    const v = parseInt(localStorage.getItem('cappuccinoFocusMl') || '50', 10); if (VOLUMES.includes(v)) milkMl = v;
    const s = parseInt(localStorage.getItem('cappuccinoFocusShots') || '1', 10); if (SHOTS.includes(s)) shots = s;
    const sw = parseInt(localStorage.getItem('cappuccinoFocusSweeteners') || '0', 10); if (SWEETENERS.includes(sw)) sweeteners = sw;
  } catch {}
  const milk = milkProducts.find(m => m.id === selectedMilkId) || milkProducts[0];
  const { food, totalG } = buildCappuccinoSynth({ milk, milkMl, shots, sweeteners });
  addFoodEntry(food, totalG, food.servingLabel, 'drinks', today);
  return food;
}

export default function CappuccinoFocusOverlay({
  open,
  onClose,
  milkProducts = [],   // pre-curated short list passed by parent
  addFoodEntry,
  today,
  onAfterLog,
}) {
  // Persisted picks
  const [selectedMilkId, setSelectedMilkId] = useState(() => {
    try { return localStorage.getItem('cappuccinoFocusMilk') || (milkProducts[0]?.id || ''); } catch { return milkProducts[0]?.id || ''; }
  });
  const [milkMl, setMilkMl] = useState(() => {
    try { const v = parseInt(localStorage.getItem('cappuccinoFocusMl') || '50', 10); return VOLUMES.includes(v) ? v : 50; } catch { return 50; }
  });
  const [shots, setShots] = useState(() => {
    try { const v = parseInt(localStorage.getItem('cappuccinoFocusShots') || '1', 10); return SHOTS.includes(v) ? v : 1; } catch { return 1; }
  });
  const [sweeteners, setSweeteners] = useState(() => {
    try { const v = parseInt(localStorage.getItem('cappuccinoFocusSweeteners') || '0', 10); return SWEETENERS.includes(v) ? v : 0; } catch { return 0; }
  });

  useEffect(() => { try { localStorage.setItem('cappuccinoFocusMilk', selectedMilkId); } catch {} }, [selectedMilkId]);
  useEffect(() => { try { localStorage.setItem('cappuccinoFocusMl', String(milkMl)); } catch {} }, [milkMl]);
  useEffect(() => { try { localStorage.setItem('cappuccinoFocusShots', String(shots)); } catch {} }, [shots]);
  useEffect(() => { try { localStorage.setItem('cappuccinoFocusSweeteners', String(sweeteners)); } catch {} }, [sweeteners]);

  // Body scroll lock + Esc to close
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);

  const selectedMilk = useMemo(
    () => milkProducts.find(m => m.id === selectedMilkId) || milkProducts[0],
    [selectedMilkId, milkProducts]
  );

  // Scale milk by the chosen volume. servingG ≈ ml for milk.
  const milkPour = useMemo(() => {
    if (!selectedMilk) return null;
    const baseG = Math.max(1, selectedMilk.servingG || 250);
    const scale = milkMl / baseG;
    return {
      protein:  (selectedMilk.protein  || 0) * scale,
      calories: (selectedMilk.calories || 0) * scale,
      carbs:    (selectedMilk.carbs    || 0) * scale,
      fat:      (selectedMilk.fat      || 0) * scale,
      fiber:    (selectedMilk.fiber    || 0) * scale,
      sugar:    (selectedMilk.sugar    || 0) * scale,
      sodium:   (selectedMilk.sodium   || 0) * scale,
    };
  }, [selectedMilk, milkMl]);

  if (!open) return null;

  // Compose the cappuccino as a SINGLE synthetic entry so the user's log
  // shows one row, not two. Espresso macros + scaled milk.
  const totalKcal = Math.round((shots * ESPRESSO.calories) + (milkPour?.calories || 0) + (sweeteners * SWEETENER.calories));
  const totalP    = Math.round(((shots * ESPRESSO.protein) + (milkPour?.protein || 0) + (sweeteners * SWEETENER.protein)) * 10) / 10;
  const totalC    = Math.round(((shots * ESPRESSO.carbs)   + (milkPour?.carbs   || 0) + (sweeteners * SWEETENER.carbs))   * 10) / 10;
  const totalF    = Math.round(((shots * ESPRESSO.fat)     + (milkPour?.fat     || 0) + (sweeteners * SWEETENER.fat))     * 10) / 10;
  const totalFi   = Math.round(((shots * ESPRESSO.fiber)   + (milkPour?.fiber   || 0) + (sweeteners * SWEETENER.fiber))   * 10) / 10;
  const totalSu   = Math.round(((shots * ESPRESSO.sugar)   + (milkPour?.sugar   || 0) + (sweeteners * SWEETENER.sugar))   * 10) / 10;
  const totalSo   = Math.round((shots * ESPRESSO.sodium)   + (milkPour?.sodium  || 0) + (sweeteners * SWEETENER.sodium));
  const totalG    = (shots * 30) + milkMl + (sweeteners * 1);

  // Shorten the milk name for the entry label.
  const shortMilkName = (() => {
    if (!selectedMilk) return "milk";
    const n = selectedMilk.name || "";
    return n
      .replace(/^Clover\s+/i, "Clover ")
      .replace(/^Woolworths\s+/i, "Woolies ")
      .replace(/^Almond Breeze\s+/i, "Almond ")
      .replace(/Milk\s*$/i, "milk")
      .trim();
  })();

  const onLog = () => {
    if (!addFoodEntry || !selectedMilk || !milkPour) { onClose && onClose(); return; }
    const sweetenerSuffix = sweeteners > 0 ? ` · ${sweeteners}× Canderel` : '';
    const synth = {
      id: genId(),
      name: `Cappuccino · ${shortMilkName} ${milkMl}ml${shots > 1 ? ` · ${shots} shots` : ''}${sweetenerSuffix}`,
      category: 'Beverages',
      calories: totalKcal,
      protein:  totalP,
      carbs:    totalC,
      fat:      totalF,
      fiber:    totalFi,
      sugar:    totalSu,
      sodium:   totalSo,
      servingG: totalG,
      servingLabel: `${shots > 1 ? `${shots} shots` : '1 shot'} + ${milkMl}ml milk${sweetenerSuffix}`,
      halal: true,
    };
    addFoodEntry(synth, totalG, synth.servingLabel, 'drinks', today);
    if (onAfterLog) onAfterLog(); else onClose && onClose();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        // Lift the sheet off the very bottom so the floating tab pill
        // doesn't obscure the sticky Log button. Calories sub-tabs sit at
        // the top, the tab pill sits ~108pt from the bottom — this padding
        // raises the sheet to float between them.
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 150px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 108px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480, maxHeight: '100%',
          background: '#0c0a07',
          borderRadius: 22,
          border: `1px solid rgba(244,238,224,0.08)`,
          fontFamily: FS, color: C.ink,
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 18px 50px rgba(0,0,0,0.55)',
        }}
      >
        {/* Scrollable content area */}
        <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: '18px 18px 12px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22 }}>☕</span>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1 }}>Quick Cappuccino</div>
              <div style={{ fontSize: 11, color: C.dim, marginTop: 4, fontFamily: FM, letterSpacing: '0.06em' }}>
                ESPRESSO + STEAMED MILK
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 34, height: 34, borderRadius: 99, border: 'none',
              background: 'rgba(244,238,224,0.08)', color: C.ink,
              fontSize: 18, lineHeight: 1, cursor: 'pointer',
            }}
          >×</button>
        </div>

        {/* Hero summary */}
        <div style={{
          padding: '18px 16px', borderRadius: 16,
          background: `linear-gradient(135deg, rgba(201,160,122,0.14), rgba(201,160,122,0.04))`,
          border: `1px solid rgba(201,160,122,0.25)`,
          marginBottom: 18,
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11, color: C.dim, fontFamily: FM, letterSpacing: '0.08em' }}>TOTAL</span>
            <span style={{ fontSize: 11, color: C.dim }}>{totalG}g · {milkMl}ml milk</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginTop: 6 }}>
            <span style={{ fontSize: 36, fontWeight: 900, color: C.coffee, fontFamily: FS, lineHeight: 1, letterSpacing: '-1.5px' }}>
              {totalKcal}
            </span>
            <span style={{ fontSize: 13, color: C.dim }}>kcal</span>
          </div>
          <div style={{ display: 'flex', gap: 14, marginTop: 10, fontFamily: FM, fontSize: 11 }}>
            <span><span style={{ color: C.cyan, fontWeight: 800 }}>{totalP}g</span> <span style={{ color: C.dim }}>P</span></span>
            <span><span style={{ color: C.amber, fontWeight: 800 }}>{totalC}g</span> <span style={{ color: C.dim }}>C</span></span>
            <span><span style={{ color: C.coral, fontWeight: 800 }}>{totalF}g</span> <span style={{ color: C.dim }}>F</span></span>
            {totalSu > 1 && <span><span style={{ color: C.amber, fontWeight: 800 }}>{totalSu}g</span> <span style={{ color: C.dim }}>sugar</span></span>}
          </div>
        </div>

        {/* Milk picker */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, color: C.dim, fontFamily: FM, letterSpacing: '0.08em', marginBottom: 8 }}>MILK</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {milkProducts.map(m => {
              const sel = m.id === selectedMilkId;
              const baseG = Math.max(1, m.servingG || 250);
              const pourCal = Math.round((m.calories / baseG) * milkMl);
              const pourP   = Math.round(((m.protein / baseG) * milkMl) * 10) / 10;
              return (
                <button
                  key={m.id}
                  onClick={() => setSelectedMilkId(m.id)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 14px', borderRadius: 12,
                    border: `1px solid ${sel ? C.coffee : 'rgba(244,238,224,0.10)'}`,
                    background: sel ? 'rgba(201,160,122,0.10)' : 'rgba(244,238,224,0.03)',
                    color: C.ink, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                    transition: 'all 0.12s ease',
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: sel ? 800 : 600 }}>{m.name}</span>
                  <span style={{ fontFamily: FM, fontSize: 11, color: sel ? C.coffee : C.dim }}>
                    {pourCal} kcal · {pourP}p
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Volume picker */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, color: C.dim, fontFamily: FM, letterSpacing: '0.08em', marginBottom: 8 }}>MILK VOLUME</div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${VOLUMES.length}, 1fr)`, gap: 6 }}>
            {VOLUMES.map(v => {
              const sel = v === milkMl;
              return (
                <button
                  key={v}
                  onClick={() => setMilkMl(v)}
                  style={{
                    padding: '12px 6px', borderRadius: 10,
                    border: `1px solid ${sel ? C.coffee : 'rgba(244,238,224,0.10)'}`,
                    background: sel ? C.coffee : 'transparent',
                    color: sel ? '#1a0f06' : C.ink,
                    fontFamily: FM, fontSize: 13, fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >{v}ml</button>
              );
            })}
          </div>
        </div>

        {/* Shots picker */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, color: C.dim, fontFamily: FM, letterSpacing: '0.08em', marginBottom: 8 }}>ESPRESSO SHOTS</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
            {SHOTS.map(s => {
              const sel = s === shots;
              return (
                <button
                  key={s}
                  onClick={() => setShots(s)}
                  style={{
                    padding: '14px 6px', borderRadius: 10,
                    border: `1px solid ${sel ? C.coffee : 'rgba(244,238,224,0.10)'}`,
                    background: sel ? C.coffee : 'transparent',
                    color: sel ? '#1a0f06' : C.ink,
                    fontFamily: FM, fontSize: 14, fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >{s} shot{s > 1 ? 's' : ''}</button>
              );
            })}
          </div>
        </div>

        {/* Sweetener picker — Canderel with sucralose */}
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 11, color: C.dim, fontFamily: FM, letterSpacing: '0.08em', marginBottom: 8 }}>
            CANDEREL <span style={{ color: C.faint }}>(SUCRALOSE)</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {SWEETENERS.map(n => {
              const sel = n === sweeteners;
              return (
                <button
                  key={n}
                  onClick={() => setSweeteners(n)}
                  style={{
                    padding: '14px 6px', borderRadius: 10,
                    border: `1px solid ${sel ? C.coffee : 'rgba(244,238,224,0.10)'}`,
                    background: sel ? C.coffee : 'transparent',
                    color: sel ? '#1a0f06' : C.ink,
                    fontFamily: FM, fontSize: 14, fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >{n === 0 ? 'none' : `${n} stick${n > 1 ? 's' : ''}`}</button>
              );
            })}
          </div>
        </div>

        </div>
        {/* Sticky Log button — always visible above the floating tab bar. */}
        <div
          style={{
            flex: '0 0 auto',
            padding: '12px 18px 16px',
            background: 'linear-gradient(to bottom, rgba(12,10,7,0), rgba(12,10,7,0.95) 30%, #0c0a07 60%)',
            borderTop: `1px solid rgba(244,238,224,0.06)`,
            borderBottomLeftRadius: 22, borderBottomRightRadius: 22,
          }}
        >
          <button
            onClick={onLog}
            style={{
              width: '100%', padding: '16px', borderRadius: 14,
              background: C.coffee, color: '#1a0f06',
              fontFamily: FS, fontSize: 15, fontWeight: 900, letterSpacing: '0.02em',
              border: 'none', cursor: 'pointer',
              boxShadow: '0 6px 22px rgba(201,160,122,0.30)',
            }}
          >
            Log cappuccino · {totalKcal} kcal
          </button>
        </div>
      </div>
    </div>
  );
}
